<template>
  <div class="rule-details">
    <div class="page-header">
      <Breadcrumb>
        <BreadcrumbItem :to="`/nodes/${nodeID}/rules`">{{ this.$t('rules.rules') }}</BreadcrumbItem>
        <BreadcrumbItem>{{ accessTitle }}</BreadcrumbItem>
      </Breadcrumb>
      <div v-if="accessType === 'create'" class="mode-swtich">
        <label for="">{{ isTextMode ? $t('rules.textMode') : $t('rules.visualizationMode') }}</label>
        <i-switch v-model="isTextMode" @change="handleModeChange"></i-switch>
      </div>
    </div>
    <Card border shadow class="detail">
      <Form ref="record" :model="record" :rules="isView ? {} : rules">
        <Row>
          <i-col span="16">
            <FormItem label="Rule ID" prop="id">
              <i-input v-model="record.id" size="large" :disabled="idDisabled"></i-input>
            </FormItem>
          </i-col>
          <i-col span="16">
            <FormItem :label="$t('common.name')">
              <i-input v-model="record.name" size="large"></i-input>
            </FormItem>
          </i-col>
          <i-col span="16">
            <FormItem :label="$t('rules.runImmediately')">
              <div class="switch-wraper">
                <i-switch v-model="record.triggered" />
              </div>
            </FormItem>
          </i-col>
          <!-- Text -->
          <i-col v-if="isTextMode" span="16">
            <FormItem prop="text" class="code-editor--item">
              <template v-slot:label>
                <span>{{ $t('rules.text') }}</span>
                <Tooltip
                  class="fields-table--tip"
                  :max-width="350"
                  transfer
                  :content="$t('rules.textTip')"
                  placement="right"
                >
                  <Icon type="md-help-circle"> </Icon>
                </Tooltip>
              </template>
              <div class="monaco-container">
                <Monaco id="record-text" v-model="record.text" lang="json" :disabled="isView" backgroud="#282a36">
                </Monaco>
              </div>
            </FormItem>
          </i-col>
          <!-- Visualization -->
          <template v-if="!isTextMode">
            <i-col span="16">
              <FormItem label="SQL" prop="sql" class="code-editor--item">
                <div class="monaco-container">
                  <Monaco
                    id="record-sql"
                    v-if="sqlProvider.length"
                    v-model="record.sql"
                    lang="sql"
                    :disabled="isView"
                    backgroud="#282a36"
                    :provider="sqlProvider"
                  >
                  </Monaco>
                </div>
              </FormItem>
            </i-col>
            <i-col span="16">
              <FormItem :label="$t('rules.actions')" prop="actions" class="action-table">
                <Actions ref="actions" :nodeID="nodeID" :actions="record.actions" :isDetail="isView"> </Actions>
              </FormItem>
            </i-col>
            <i-col span="16" v-if="showOptions">
              <Divider>
                <a href="javascript:;" @click="showOptionContent = !showOptionContent">
                  <Icon v-if="showOptionContent" type="ios-arrow-up" />
                  <Icon v-else type="ios-arrow-down" />
                  {{ $t('rules.options') }}
                </a>
              </Divider>
            </i-col>
            <!-- options -->
            <i-col v-if="showOptionContent" span="16">
              <Row :gutter="20">
                <i-col span="12">
                  <FormItem :label="$t('rules.isEventTime')" prop="options.isEventTime">
                    <Tooltip max-width="250px" transfer :content="$t('rules.isEventTimeDesc')" placement="top">
                      <Icon type="md-help-circle"> </Icon>
                    </Tooltip>
                    <div class="radio-control">
                      <span class="radio-item">
                        <input
                          :disabled="isView"
                          id="true-event-time-radio"
                          type="radio"
                          v-model="record.options.isEventTime"
                          :value="true"
                        />
                        <label for="true-event-time-radio">True</label>
                      </span>
                      <span class="radio-item">
                        <input
                          :disabled="isView"
                          id="false-event-time-radio"
                          type="radio"
                          v-model="record.options.isEventTime"
                          :value="false"
                        />
                        <label for="false-event-time-radio">False</label>
                      </span>
                    </div>
                  </FormItem>
                </i-col>
                <i-col span="12">
                  <FormItem :label="$t('rules.sendMetaToSink')" prop="options.sendMetaToSink">
                    <Tooltip max-width="250px" transfer :content="$t('rules.sendMetaToSinkDesc')" placement="top">
                      <Icon type="md-help-circle"> </Icon>
                    </Tooltip>
                    <div class="radio-control">
                      <span class="radio-item">
                        <input
                          :disabled="isView"
                          id="true-send-meta-radio"
                          type="radio"
                          v-model="record.options.sendMetaToSink"
                          :value="true"
                        />
                        <label for="true-send-meta-radio">True</label>
                      </span>
                      <span class="radio-item">
                        <input
                          :disabled="isView"
                          id="false-send-meta-radio"
                          type="radio"
                          v-model="record.options.sendMetaToSink"
                          :value="false"
                        />
                        <label for="false-send-meta-radio">False</label>
                      </span>
                    </div>
                  </FormItem>
                </i-col>
                <i-col span="12">
                  <FormItem :label="$t('rules.lateTolerance')" prop="options.lateTolerance">
                    <Tooltip max-width="250px" transfer :content="$t('rules.lateToleranceDesc')" placement="top">
                      <Icon type="md-help-circle"> </Icon>
                    </Tooltip>
                    <i-input
                      v-model.number="record.options.lateTolerance"
                      size="large"
                      type="number"
                      placeholder="0"
                      :disabled="isView"
                    ></i-input>
                  </FormItem>
                </i-col>
                <i-col span="12">
                  <FormItem :label="$t('rules.concurrency')" prop="options.concurrency">
                    <Tooltip max-width="250px" transfer :content="$t('rules.concurrencyDesc')" placement="top">
                      <Icon type="md-help-circle"> </Icon>
                    </Tooltip>
                    <i-input
                      v-model.number="record.options.concurrency"
                      size="large"
                      type="number"
                      placeholder="1"
                      :disabled="isView"
                    ></i-input>
                  </FormItem>
                </i-col>
                <i-col span="12">
                  <FormItem :label="$t('rules.bufferLength')" prop="options.bufferLength">
                    <Tooltip max-width="250px" transfer :content="$t('rules.bufferLengthDesc')" placement="top">
                      <Icon type="md-help-circle"> </Icon>
                    </Tooltip>
                    <i-input
                      v-model.number="record.options.bufferLength"
                      size="large"
                      type="number"
                      placeholder="1024"
                      :disabled="isView"
                    ></i-input>
                  </FormItem>
                </i-col>
                <i-col span="12">
                  <FormItem :label="$t('rules.qos')" prop="options.qos">
                    <Tooltip max-width="250px" transfer :content="$t('rules.qosDesc')" placement="top">
                      <Icon type="md-help-circle"> </Icon>
                    </Tooltip>
                    <Select v-model="record.options.qos" size="large" placeholder="0" clearable :disabled="isView">
                      <Option :value="0" label="0"> </Option>
                      <Option :value="1" label="1"> </Option>
                      <Option :value="2" label="2"> </Option>
                    </Select>
                  </FormItem>
                </i-col>
                <i-col span="12">
                  <FormItem :label="$t('rules.checkpointInterval')" prop="options.checkpointInterval">
                    <Tooltip max-width="250px" transfer :content="$t('rules.checkpointIntervalDesc')" placement="top">
                      <Icon type="md-help-circle"> </Icon>
                    </Tooltip>
                    <i-input
                      v-model.number="record.options.checkpointInterval"
                      size="large"
                      type="number"
                      placeholder="300000"
                      :disabled="isView"
                    ></i-input>
                  </FormItem>
                </i-col>
                <!-- restart strategy -->
                <i-col span="12">
                  <FormItem :label="$t('rules.attempts')">
                    <Tooltip max-width="250px" transfer :content="$t('rules.attemptsDesc')" placement="top">
                      <Icon type="md-help-circle" />
                    </Tooltip>
                    <i-input
                      v-model.number="record.options.restartStrategy.attempts"
                      size="large"
                      type="number"
                      placeholder="0"
                      :disabled="isView"
                    ></i-input>
                  </FormItem>
                </i-col>
                <i-col span="12">
                  <FormItem :label="$t('rules.delay')">
                    <Tooltip max-width="250px" transfer :content="$t('rules.delayDesc')" placement="top">
                      <Icon type="md-help-circle" />
                    </Tooltip>
                    <i-input
                      v-model.number="record.options.restartStrategy.delay"
                      size="large"
                      type="number"
                      placeholder="1000"
                      :disabled="isView"
                    ></i-input>
                  </FormItem>
                </i-col>
                <i-col span="12">
                  <FormItem :label="$t('rules.maxDelay')">
                    <Tooltip max-width="250px" transfer :content="$t('rules.maxDelayDesc')" placement="top">
                      <Icon type="md-help-circle" />
                    </Tooltip>
                    <i-input
                      v-model.number="record.options.restartStrategy.maxDelay"
                      size="large"
                      type="number"
                      placeholder="30000"
                      :disabled="isView"
                    ></i-input>
                  </FormItem>
                </i-col>
                <i-col span="12">
                  <FormItem :label="$t('rules.multiplier')">
                    <Tooltip max-width="250px" transfer :content="$t('rules.delayDesc')" placement="top">
                      <Icon type="md-help-circle" />
                    </Tooltip>
                    <i-input
                      v-model.number="record.options.restartStrategy.multiplier"
                      size="large"
                      type="number"
                      placeholder="2"
                      :disabled="isView"
                    ></i-input>
                  </FormItem>
                </i-col>
                <i-col span="12">
                  <FormItem :label="$t('rules.jitterFactor')">
                    <Tooltip max-width="250px" transfer :content="$t('rules.jitterFactorDesc')" placement="top">
                      <Icon type="md-help-circle" />
                    </Tooltip>
                    <i-input
                      v-model.number="record.options.restartStrategy.jitterFactor"
                      size="large"
                      type="number"
                      placeholder="0.1"
                      :disabled="isView"
                    ></i-input>
                  </FormItem>
                </i-col>
              </Row>
            </i-col>
          </template>
          <i-col span="16" class="oper-col">
            <FormItem>
              <Button v-if="!isView" type="primary" @click="save">{{ $t('common.submit') }}</Button>
              <Button :style="!isView ? 'margin-left: 8px' : ''" type="text" :to="`/nodes/${nodeID}/rules`">
                {{ $t('common.cancel') }}
              </Button>
            </FormItem>
          </i-col>
        </Row>
      </Form>
      <Spin v-if="loading" size="large" fix> </Spin>
    </Card>
  </div>
</template>

<script>
import nodeID from '@/mixins/nodeID'
import currentLang from '@/mixins/currentLang'
import { viewRule, createRule, updateRule, loadAllFunctions } from '@/api/rules'
import { loadServicesFuncs } from '@/api/services'
import Monaco from '@/components/Monaco'
import dataTypeProviderData from '@/utils/dataTypeProviderData'
import sqlKeyWords from '@/utils/sqlKeyWords'
import Actions from './components/Actions'

export default {
  name: 'RuleDetail',

  components: {
    Monaco,
    Actions,
  },

  mixins: [nodeID, currentLang],

  data() {
    return {
      id: this.$route.params.ruleID,
      accessType: this.$route.query.oper,
      loading: false,
      showOptions: true,
      showOptionContent: false,
      isTextMode: false,
      record: {
        id: '',
        name: '',
        triggered: false,
        text: '',
        actions: [],
        options: {
          restartStrategy: {},
        },
        sql: '',
      },
      rules: {
        id: [{ required: true, message: this.$t('rules.idRequired') }],
        sql: [{ required: true, message: this.$t('streams.sqlRequired') }],
        actions: [{ required: true, message: this.$t('rules.actionsRequired') }],
        text: [{ required: true, message: this.$t('common.fillContent') }],
      },
      sqlProvider: [],
    }
  },

  computed: {
    isView() {
      const { oper } = this.$route.query
      return oper === 'view'
    },
    accessTitle() {
      const { oper } = this.$route.query
      const operDict = {
        view: this.$t('common.view'),
        create: this.$t('common.create'),
        edit: this.$t('common.edit'),
      }
      return operDict[oper]
    },
    idDisabled() {
      return this.isView || this.accessType === 'edit'
    },
  },

  created() {
    if (this.accessType !== 'create') {
      this.loadDetail()
    }
    this.loadRuleFunctions()
  },

  methods: {
    loadRuleFunctions() {
      loadAllFunctions(this.nodeID).then((res) => {
        const functionList = []
        res.data.forEach((item) => {
          functionList.push(...item.functions)
        })
        const functionProviderData = []
        functionList.forEach((val) => {
          const tempObj = {
            name: val.name,
            type: 'Method',
            detail: `eKuiper ${this.$t('sql.example')}${val.example}`,
            documentation: this.i18nLabel(val.hint),
          }
          functionProviderData.push(tempObj)
        })
        loadServicesFuncs(this.nodeID).then((servicesFuncs) => {
          servicesFuncs.data.forEach((func) => {
            const tempObj = {
              name: func,
              type: 'Method',
              detail: 'eKuiper services function',
              documentation: '',
            }
            functionProviderData.push(tempObj)
          })
          this.sqlProvider = [...dataTypeProviderData, ...functionProviderData, ...sqlKeyWords.ruleSqlKeyWords]
        })
      })
    },
    handleModeChange() {
      setTimeout(() => {
        this.$refs.record.resetFields()
      }, 1000)
    },
    resetActions(actions) {
      const res = actions
        ? actions.map((action) => {
            const sink = Object.keys(action)[0]
            return {
              data: action,
              sink,
            }
          })
        : []
      return res
    },
    // get rule detail
    loadDetail() {
      this.loading = true
      viewRule(this.nodeID, this.id)
        .then((res) => {
          const { id, name, actions, triggered, options, sql, ...resetData } = res.data
          const { restartStrategy = {}, ...restOptions } = options || {}
          const optionsData = {
            ...restOptions,
            restartStrategy,
          }
          this.record = {
            id,
            name,
            triggered: triggered || false,
            sql: sql || '',
            actions: this.resetActions(actions),
            options: optionsData,
            ...resetData,
          }
          this.showOptions = true
        })
        .finally(() => {
          this.loading = false
        })
    },

    parseRuleData(data) {
      const { id, name, triggered, sql, actions, options } = data
      const ruleData = {
        id,
        name,
        triggered,
        sql,
        actions: [],
        options: { ...options },
      }
      ruleData.actions = actions.map((action) => {
        const actionMap = {}
        const { sink } = action
        actionMap[sink] = {
          ...action.data[sink],
        }
        return actionMap
      })
      return ruleData
    },
    save() {
      this.$refs.record.validate((valid) => {
        if (!valid) {
          return
        }
        let data = {}
        if (this.isTextMode) {
          const { id, name, triggered } = this.record
          data = {
            id,
            name,
            triggered,
            ...JSON.parse(this.record.text),
          }
        } else {
          data = this.parseRuleData({ ...this.record })
        }

        if (this.accessType === 'create') {
          createRule(this.nodeID, data).then((res) => {
            if (res.status === 201 && res.statusText === 'Created') {
              this.$Message.success(res.data)
            } else {
              this.$Message.success(this.$t('common.addSuccess'))
            }
            this.$router.push(`/nodes/${this.nodeID}/rules`)
          })
        } else {
          updateRule(this.nodeID, this.record.id, data).then((res) => {
            if (res.status === 200 && res.statusText === 'OK') {
              this.$Message.success(res.data)
            } else {
              this.$Message.success(this.$t('common.editSuccess'))
            }
            this.$router.push(`/nodes/${this.nodeID}/rules`)
          })
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep {
  .ivu-form .ivu-form-item-label {
    position: relative;
  }
  .ivu-icon-md-help-circle {
    font-size: 16px;
    position: relative;
    top: 0px;
    left: 2px;
  }
  .code-editor {
    border: 1px solid #495062;
  }
  .ivu-divider {
    margin: 24px 0;
  }
  .ivu-alert {
    margin: 10px 0px 20px 0px;
  }
  .ivu-alert-info {
    color: #a8acb6;
    border-color: var(--color-border-tab);
    background: var(--color-border-tab);
  }
  .oper-col {
    margin-top: 20px;
  }

  .code-editor--item .ivu-form-item-label {
    height: 34px;
  }
  .monaco-container {
    margin-top: 34px; // 34 = form-item-label--height
  }
}
.switch-wraper {
  margin-top: 32px;
  width: 100%;
  margin-top: 32px;
  display: flex;
}
</style>
